import {
  HUBSPOT_MARKETING_INFORMATION_SUBSCRIPTION_ID,
  type ContactFormMetadata,
} from '@transcend-io/contact-form-schema';
import { TrackingEvent } from '@transcend-io/segment/core';

/**
 * Hardcoded HubSpot / analytics config for Rails waitlist email capture.
 * Kept out of Sanity so campaign routing stays consistent.
 */
export const RAILS_HUBSPOT_CAMPAIGN_ID =
  '51761078-7c5c-4506-aab5-9ec713dbbed8' as const;

export const RAILS_FORM_TITLE = 'Rails Waitlist';

export const RAILS_FORM_METADATA = {
  hubspotCampaignId: RAILS_HUBSPOT_CAMPAIGN_ID,
  hubspotConvertingFormType: 'lead_contact_form' as const,
  hubspotEmailSubscriptionIds: [HUBSPOT_MARKETING_INFORMATION_SUBSCRIPTION_ID],
  trackingEventName: TrackingEvent.enum['Contact Form Submitted'],
  formTitle: RAILS_FORM_TITLE,
} satisfies Pick<
  ContactFormMetadata,
  | 'hubspotCampaignId'
  | 'hubspotConvertingFormType'
  | 'trackingEventName'
  | 'formTitle'
> & {
  hubspotEmailSubscriptionIds: string[];
};

export const RAILS_LANDING_TRACKING_EVENT =
  TrackingEvent.enum['Contact Form Submission Started'];

/** Default host for the post-submit Rails product handoff. */
export const RAILS_JUMP_ORIGIN_DEFAULT = 'https://withrails.ai';

/**
 * Parse the configured jump base. Query params on the origin (or in
 * `NEXT_PUBLIC_RAILS_JUMP_SEARCH`) are preserved on the final `/jump` URL —
 * useful for Vercel deployment-protection bypass tokens.
 *
 * Examples:
 * - `NEXT_PUBLIC_RAILS_JUMP_ORIGIN=http://localhost:3001`
 * - `NEXT_PUBLIC_RAILS_JUMP_ORIGIN=https://preview.vercel.app?x-vercel-protection-bypass=…`
 * - `NEXT_PUBLIC_RAILS_JUMP_SEARCH=x-vercel-protection-bypass=…` (merged with origin)
 */
function getRailsJumpBase(): URL {
  const fromEnv = process.env.NEXT_PUBLIC_RAILS_JUMP_ORIGIN?.trim();
  const raw =
    fromEnv && fromEnv.length > 0 ? fromEnv : RAILS_JUMP_ORIGIN_DEFAULT;
  return new URL(raw.includes('://') ? raw : `https://${raw}`);
}

/** Product handoff URL after Rails waitlist email is submitted. */
export function getRailsJumpUrl(submissionId: string): string {
  const base = getRailsJumpBase();
  const url = new URL('/jump', base.origin);

  base.searchParams.forEach((value, key) => {
    url.searchParams.set(key, value);
  });

  const extraSearch = process.env.NEXT_PUBLIC_RAILS_JUMP_SEARCH?.trim();
  if (extraSearch) {
    const extras = new URLSearchParams(
      extraSearch.startsWith('?') ? extraSearch.slice(1) : extraSearch,
    );
    extras.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
  }

  url.searchParams.set('id', submissionId);
  url.searchParams.set('success', 'true');
  return url.toString();
}
